/**
 *
 *  CodeVerification for PasswordReset
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 08/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAppState } from "../../../state";
import { useSignUpState } from "../../../state/SignUpState";

// Components
import Button from "../../Button/Button";

// Icons
import ArrowRightIcon from "../../../icons/ArrowRightIcon";

const MAX_INPUTS = 6;
const TIME_TO_WAIT_BEFORE_SEND_AGAIN = 60;

const CodeInput = ({ className, index, hasError, onKeyDown, onInput }) => {
  const handleKeydown = useCallback(
    (e) => {
      onKeyDown && onKeyDown(e, index);
    },
    [index, onKeyDown]
  );

  const handleChange = useCallback(
    (e) => {
      onInput && onInput(e, index);
    },
    [index, onInput]
  );

  return (
    <input
      type="tel"
      className={`${className} tbk-input-field tbk-text-title-medium tbk-mb-auto tbk-mr-1 tbk-pl-1 tbk-pr-1 tbk-text-center ${
        hasError ? "tbk-border-coral" : "tbk-border-grey-inactive"
      }`}
      onKeyDown={handleKeydown}
      onInput={handleChange}
    />
  );
};

const SendCodeAgainButton = ({ onClick }) => {
  const [sendCodeAgainTime, setSendCodeAgainTime] = useState(
    TIME_TO_WAIT_BEFORE_SEND_AGAIN
  );

  const intervalRef = useRef(null);
  const sendCodeAgainTimeRef = useRef(0);

  const startTimer = () => {
    sendCodeAgainTimeRef.current = TIME_TO_WAIT_BEFORE_SEND_AGAIN;

    intervalRef.current = setInterval(() => {
      sendCodeAgainTimeRef.current--;

      if (sendCodeAgainTimeRef.current <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;

        // Fix 0s on clicking the button
        setSendCodeAgainTime(TIME_TO_WAIT_BEFORE_SEND_AGAIN);

        return;
      }

      setSendCodeAgainTime(sendCodeAgainTimeRef.current);
    }, 1000);
  };

  useEffect(() => {
    startTimer();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const handleClick = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    } else {
      startTimer();
    }

    onClick && onClick();
  }, [onClick]);

  return (
    <button
      className={`tbk-text-small tbk-text-grey hover:tbk-text-grey-inactive ${
        intervalRef.current !== null ? "tbk-cursor-disabled" : "tbk-underline"
      }`}
      disabled={intervalRef.current !== null}
      onClick={handleClick}
    >
      Send code again{" "}
      {intervalRef.current !== null ? `in ${sendCodeAgainTime}s` : ""}
    </button>
  );
};

export default function CodeVerification({ phoneCode, phoneNumber }) {
  const [canSend, setCanSend] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { sendResetCode, verifyPhoneNumberPasswordReset, isFetching } =
    useAppState();
  const { setAuthState, setResetCode, userPhoneCode, userPhoneNumber } =
    useSignUpState();

  phoneCode = phoneCode || userPhoneCode;
  phoneNumber = phoneNumber || userPhoneNumber;

  const codeInputsContainerRef = useRef();

  const triggerInputCodeChangeEvent = (ele, value) => {
    let lastValue = "pls help me trigger this event";

    if (!ele) {
      return;
    }

    ele.value = value;

    let ev = new Event("input", { bubbles: true });

    ev.simulated = true;

    let tracker = ele._valueTracker;

    if (tracker) {
      tracker.setValue(lastValue);
    }

    ele.dispatchEvent(ev);
  };

  const handleCodeInputKeydown = (e, index) => {
    if (e.keyCode === 8 && e.target.value === "" && index > 0) {
      codeInputsContainerRef.current.childNodes[index - 1].focus();
    }
  };

  const handleCodeInputChange = (e, index) => {
    const regExpr = new RegExp(/[^0-9.]/g);

    if (regExpr.test(e.target.value)) {
      e.target.value = e.target.value.replace(regExpr, "");
    }

    const [first, ...rest] = e.target.value;

    e.target.value = first ?? ""; // first will be undefined when backspace was entered, so set the input to ""

    if (!index && e.inputIndex) {
      index = e.inputIndex;
    }

    const lastInputBox = index === MAX_INPUTS - 1;
    const didInsertContent = first !== undefined;

    if (didInsertContent && !lastInputBox) {
      let codeInput = codeInputsContainerRef.current.childNodes[index + 1];

      codeInput.focus();
      triggerInputCodeChangeEvent(codeInput, rest.join(""));
    }

    setCanSend(isCodeValid());
    setHasError(false);
  };

  const getCode = () =>
    codeInputsContainerRef.current == null
      ? ""
      : [].map
          .call(codeInputsContainerRef.current.childNodes, ({ value }) => value)
          .join("");

  const isCodeValid = () => {
    let code = getCode();

    if (code && code.length === 6) {
      return true; //setCanSend(true);
    }

    return false;
  };

  const sendCodeAgain = useCallback(() => {
    if (phoneCode && phoneNumber) {
      sendResetCode({ phoneCode, phoneNumber }).then(() => {});
    }
  }, [phoneCode, phoneNumber, verifyPhoneNumberPasswordReset]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      const code = getCode();

      if (code) {
        verifyPhoneNumberPasswordReset(phoneCode, phoneNumber, code)
          .then(() => {
            setResetCode(code);
            setAuthState("passwordReset");
          })
          .catch((err) => {
            setHasError(true);
          });
      }
    },
    [phoneCode, phoneNumber]
  );

  useEffect(() => {
    if (
      codeInputsContainerRef.current &&
      codeInputsContainerRef.current.childNodes[0]
    ) {
      codeInputsContainerRef.current.childNodes[0].focus();
    }
  }, []);

  return (
    <>
      <div className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">
        6 Digit Code
      </div>
      <p className="tbk-text-main tbk-mb-4 tbk-text-grey-main">
        We sent a verification code to your phone number {phoneCode}{" "}
        {phoneNumber} to reset your current password.
      </p>
      <form onSubmit={handleSubmit}>
        <div
          ref={codeInputsContainerRef}
          className="code-inputs-container after:tbk-text-cta tbk-mb-2 tbk-flex tbk-items-center after:tbk-absolute after:tbk-left-1/2 after:tbk-translate-x-[-9px] after:tbk-content-['-']"
        >
          {[...new Array(MAX_INPUTS)].map((v, index) => (
            <CodeInput
              key={index}
              className={index === 2 ? "tbk-mr-[27px]" : ""}
              index={index}
              hasError={hasError}
              onKeyDown={handleCodeInputKeydown}
              onInput={handleCodeInputChange}
            />
          ))}
        </div>
        {hasError ? (
          <div className="tbk-text-main tbk-mt-[-8px] tbk-mb-2 tbk-text-center tbk-text-coral">
            Code is incorrect, try again
          </div>
        ) : null}

        <Button
          type="submit"
          className="tbk-mb-3 tbk-w-full "
          disabled={!canSend || isFetching}
          showLoader={isFetching}
          endIcon={<ArrowRightIcon />}
        >
          Submit
        </Button>
      </form>
      <SendCodeAgainButton onClick={sendCodeAgain} />
    </>
  );
}
